import {
    ChangeDetectionStrategy,
    Component,
    OnDestroy,
    OnInit,
    ViewChild,
} from '@angular/core';
import {
    FormBuilder,
    FormControl,
    FormGroup,
    Validators,
} from '@angular/forms';
import {
    AppStateFacade,
    Setting,
} from '@app-state';
import { UiModalComponent } from '@components/ui-modal/ui-modal.component';
import {
    FeatureFlag,
    IPasswordComplexity,
    IUser,
    ModalResultType,
    RobotTypeAttended,
    UserType,
} from '@models';
import { TranslateService } from '@ngx-translate/core';
import { ProvisionRobotComponent } from '@pages/users/components/provision-robot/provision-robot.component';
import {
    AuthService,
    ModalService,
    RoleService,
    SignalRService,
    UserService,
} from '@services';
import { DEFAULT_NOTIFICATION_SETTINGS } from '@services/user.service';
import { ISuggestValue } from '@uipath/angular/components/ui-suggest';
import UiValidators, {
    UI_EMAIL_DOMAIN_MAX_LENGTH,
    UI_NAME_MAX_LENGTH,
    UI_PASSWORD_MAX_LENGTH,
    UI_USERNAME_MAX_LENGTH,
} from '@validators';

import cloneDeep from 'lodash-es/cloneDeep';
import {
    BehaviorSubject,
    iif,
    Observable,
    of,
    Subject,
} from 'rxjs';
import {
    finalize,
    map,
    switchMap,
    takeUntil,
} from 'rxjs/operators';

@Component({
    templateUrl: './user.modal.html',
    providers: [
        FormBuilder,
        RoleService,
    ],
    styleUrls: ['./user.modal.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UserModalComponent
    extends UiModalComponent<IUser>
    implements OnInit, OnDestroy {

    public get isCurrentUser() {
        return this._appState.user.value!.id === this.context.data.id;
    }

    public get isRobotProvisionAvailable() {
        return this.context.data.type !== UserType.Robot &&
            this._appState.settings.get(Setting.isModernFolderSystemEnabled);
    }

    @ViewChild(ProvisionRobotComponent, { static: false })
    public set provisionRobot(value: ProvisionRobotComponent) {
        if (!value) {
            this.form.removeControl('robotProvision');
            return;
        }

        this.form.addControl(
            'robotProvision',
            value.form,
        );
    }

    public form!: FormGroup;
    public loading$ = new BehaviorSubject(false);
    public selectedOrganizations: Array<{ id: number, text: string }> = [];
    public organizationUnits?: Array<{ id: number, text: string }>;
    public disallowEmailChange = false;
    public disallowPasswordChange = false;
    public UserType = UserType;
    public isDomainVisible?: boolean;
    public isWindowsAuthenticationEnabled?: boolean;
    public passwordComplexity?: IPasswordComplexity;
    public title!: string;

    public isRobotSwitcherEnabled$!: Observable<boolean>;
    public isRobotEditable$!: Observable<boolean>;

    private _appState = this._injector.get<AppStateFacade>(AppStateFacade);
    private _authService = this._injector.get<AuthService>(AuthService);
    private _userService = this._injector.get<UserService>(UserService);
    private _modalService = this._injector.get<ModalService>(ModalService);
    private _translateService = this._injector.get<TranslateService>(TranslateService);
    private _signalRService = this._injector.get<SignalRService>(SignalRService);
    private _roleService = this._injector.get<RoleService>(RoleService);

    private _destroyed$ = new Subject();

    ngOnInit() {
        if (this.context.isNewEntry) {
            this.context.data.type = this.context.additionalData.type;
        }

        this.isRobotSwitcherEnabled$ = iif(
            () => this.context.data.mayHaveRobotSession,
            this._appState.user.permissions.isGranted('Robots.Delete'),
            this._appState.user.permissions.isGranted('Robots.Create'),
        );

        this.isRobotEditable$ = iif(
            () => this.context.data.mayHaveRobotSession,
            this._appState.user.permissions.isGranted('Robots.Edit'),
            this._appState.user.permissions.isGranted('Robots.Create'),
        );

        const editTitle = {
            [UserType.DirectoryGroup]: 'user_modal_title_edit_group',
            [UserType.DirectoryUser]: 'user_modal_title_edit_directory_user',
            [UserType.Robot]: 'user_modal_title_edit_robot',
            [UserType.User]: 'user_modal_title_edit',
        };

        this.title = this.context.isNewEntry
            ? 'users_grid_action_add'
            : editTitle[this.context.data.type] || 'user_modal_title_edit';

        const fb = this._injector.get<FormBuilder>(FormBuilder);

        this.isDomainVisible = this._appState.settings.get(Setting.isWindowsAuthEnabled) &&
            !this._appState.settings.isFeatureEnabled(FeatureFlag.DirectoryService);
        this.isWindowsAuthenticationEnabled = this._appState.settings.get(Setting.isWindowsAuthEnabled);
        this.passwordComplexity = this._appState.settings.passwordComplexity;

        this.context.data.rolesList = this.context.data.rolesList || [];
        this.context.data.organizationUnits = this.context.data.organizationUnits || [];

        if (
            this.context.data.licenseType ||
            this.context.data.isExternalLicensed
        ) {
            this.context.data.robotProvision = this.context.data.robotProvision || { type: null };
            Object.assign(this.context.data.robotProvision, {
                type: this.context.data.licenseType,
                isExternalLicensed: this.context.data.isExternalLicensed,
            });
        }

        this.disallowEmailChange = !this._appState.authConfig.value.cloud.allowSelfEmailUpdate;
        this.disallowPasswordChange = !this._appState.authConfig.value.cloud.allowPasswordChange;

        const userName = [
            this.context.data.userName,
            Validators.compose([
                Validators.required,
                Validators.maxLength(UI_USERNAME_MAX_LENGTH),
            ]),
        ];

        const name = [
            this.context.data.name,
            Validators.compose([
                Validators.maxLength(UI_NAME_MAX_LENGTH),
            ]),
        ];

        const surname = [
            this.context.data.surname,
            Validators.compose([
                Validators.maxLength(UI_NAME_MAX_LENGTH),
            ]),
        ];

        const emailAddress = [
            this.context.data.emailAddress,
            Validators.compose([
                Validators.maxLength(UI_EMAIL_DOMAIN_MAX_LENGTH),
                UiValidators.email(true),
            ]),
        ];

        const password = [
            this.context.data.password,
            Validators.compose([
                Validators.maxLength(UI_PASSWORD_MAX_LENGTH),
                UiValidators.complexity(this.passwordComplexity!, false),
            ]),
        ];

        const bypassBasicAuthRestriction = [
            this.context.data.bypassBasicAuthRestriction,
        ];

        const confirmPassword = [''];

        const rolesList = [this.context.data.rolesList.map(this._roleToSuggest)];

        const organizationUnits = [this.context.data.organizationUnits];

        if (this.context.data.type === UserType.User) {
            this.form = fb.group({
                userName,
                name,
                surname,
                emailAddress,
                password,
                confirmPassword,
                rolesList,
                organizationUnits,
                ...(this.isBasicAuthRestricted ? { bypassBasicAuthRestriction } : {}),
                'mayHaveUserSession': [{
                    value: this.context.data.mayHaveUserSession != null
                        ? this.context.data.mayHaveUserSession
                        : true,
                    disabled: this.isCurrentUser,
                }],
            }, { validator: UiValidators.confirmPassword('password', 'confirmPassword') });
        } else {
            this.form = fb.group({
                rolesList,
                organizationUnits,
                'mayHaveUserSession': [{
                    value: this.context.data.mayHaveUserSession,
                    disabled: this.isCurrentUser,
                }],
            });
        }

        if (this.isRobotProvisionAvailable) {
            this.form.addControl('mayHaveRobotSession', new FormControl(this.context.data.mayHaveRobotSession || false));
        }

        if (this.context.isNewEntry) {
            this.context.data.rolesList = [];
            this.context.data.organizationUnits = [];
        }

        this._appState.user.permissions.isGranted('Users.Edit')
            .pipe(
                map(hasUserEdit => hasUserEdit || this.context.isNewEntry),
                takeUntil(this._destroyed$),
            ).subscribe(hasUserEdit => {
                Object.keys(this.form.controls)
                    .filter(key => !['robotProvision', 'mayHaveRobotSession'].includes(key))
                    .forEach(key => this.form.get([key])![hasUserEdit ? 'enable' : 'disable']());
            });
    }

    ngOnDestroy() {
        this._destroyed$.next();
        this._destroyed$.complete();
    }

    public roleSearchFactory = (...args: any) =>
        this._appState.user.permissions
            .isGranted('Roles.View')
            .pipe(
                switchMap(result => result ?
                    this._roleService.suggest.apply(null, args) :
                    of(this.form.get('rolesList')!.value),
                ),
            )

    public onSubmit() {
        if (
            !this.form.valid ||
            this.loading$.value
        ) {
            return;
        }

        const passwordControl = this.form.get('password');
        if (this.isCurrentUser && passwordControl && passwordControl.value) {
            this._modalService.onConfirm(null, () => {
                this._submit();
            }, () => { }, this._translateService.instant('user_modal_password_warning'));
        } else {
            this._submit();
        }
    }

    public get isBasicAuthRestricted() {
        return this._appState.settings.isFeatureEnabled(FeatureFlag.RestrictBasicAuth);
    }

    private _roleToSuggest(role: string) {
        return {
            id: role,
            text: role,
        };
    }

    private async _submit() {
        this.loading$.next(true);
        const payload = cloneDeep(this.context.data);

        Object.assign(payload, cloneDeep(this.form.value));

        payload.rolesList = this.form.get('rolesList')!
            .value.map((item: ISuggestValue) => item.text);

        payload.notificationSettings = Object.assign(
            cloneDeep(DEFAULT_NOTIFICATION_SETTINGS),
            payload.notificationSettings,
        );

        if (
            payload.robotProvision &&
            ![RobotTypeAttended.Development, RobotTypeAttended.StudioX].includes(payload.robotProvision.type!)
        ) {
            payload.robotProvision.isExternalLicensed = false;
        }

        if (this.context.isNewEntry) {
            // FIXME: implement this in a better way
            if (this.isRobotProvisionAvailable && payload.robotProvision) {
                payload.licenseType = payload.robotProvision.type;
                payload.isExternalLicensed = payload.robotProvision.isExternalLicensed;
                delete payload.robotProvision.type;
                delete payload.robotProvision.isExternalLicensed;
            }

            return this._userService.add(payload)
                .pipe(
                    finalize(
                        () => this.loading$.next(false),
                    ),
                )
                .subscribe(
                    () => {
                        this._dialogRef.close({
                            type: ModalResultType.Add,
                        });
                    });
        }

        if (this.isRobotProvisionAvailable) {
            payload.licenseType = payload.robotProvision && payload.robotProvision.type;
            payload.isExternalLicensed = payload.robotProvision && payload.robotProvision.isExternalLicensed;
            if (payload.robotProvision) {
                delete payload.robotProvision.type;
                delete payload.robotProvision.isExternalLicensed;
            }
        }

        if (this.context.data.type === UserType.Robot) {
            delete payload.robotProvision;
        }

        const watchedGroups = this._signalRService.watched;
        const shouldUserLogout = this.isCurrentUser && this.context.data.password;
        let updateUser$: Observable<any>;

        // when the password for the current user is updated, a ping signaling the current permissions were changed is received
        // because the user is logged out. The signal would trigger a getCurrentPermissions that would fail with 401 Unauthorized.
        if (shouldUserLogout) {
            this._signalRService.disconnect();
        }

        if (
            this.isCurrentUser &&
            this.disallowEmailChange
        ) {
            delete payload['emailAddress'];
        }

        if (this.disallowPasswordChange) {
            delete payload['password'];
        }

        if (this.isCurrentUser) {
            updateUser$ = this._appState.user.update(payload);
        } else {
            updateUser$ = this._userService.update(this.context.data.id, payload);
        }

        return updateUser$
            .pipe(
                finalize(
                    () => this.loading$.next(false),
                ),
            )
            .subscribe(
                () => {
                    delete payload.password;
                    this._dialogRef.close({
                        type: ModalResultType.Update,
                        data: payload,
                    });

                    if (shouldUserLogout) {
                        this._authService.logout();
                    }
                },
                () => {
                    if (shouldUserLogout) {
                        this._signalRService.watched = watchedGroups;
                        this._signalRService.connect();
                    }
                },
            );
    }
}
